import { TextField } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import { ThemeContext } from '@vouched.id/vault';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ButtonWithLoader } from 'components/reusable/button-loader';
import { ReusableSelect } from 'components/reusable/select';
import { useGlobalState } from 'store/reducers/reducer';
import { deleteAccountVendorKey, loadAccountVendorKeys, loadAdminAccountGroups, updateAccountVendorKey } from 'store/sagas/sagas';
import './vendor-keys.css';
const PLATFORMS = [
    { id: 'mobile-sdk', name: 'Mobile SDK' },
    { id: 'js-plugin', name: 'JS Plugin' }
];
const PROVIDERS = [
    { id: 'scandit', name: 'Scandit' },
    { id: 'datadog', name: 'Datadog' }
];
const ACCOUNT_LEVEL = 'account-level';
const labelOf = (options, id) => options.find((option) => option.id === id)?.name ?? id;
const appSuffix = (appIdentifier) => appIdentifier === null ? '' : ` for app ${appIdentifier}`;
const rowKey = (row) => `${row.accountGroupId ?? ACCOUNT_LEVEL}:${row.platform}:${row.providerId}:${row.appIdentifier ?? ''}`;
export const VendorKeys = () => {
    const { tokens } = useContext(ThemeContext).theme;
    const { asyncDispatch } = useGlobalState();
    const { id } = useParams();
    const [rows, setRows] = useState([]);
    const [groupNames, setGroupNames] = useState(new Map());
    const [status, setStatus] = useState('loading');
    const [isSaving, setIsSaving] = useState(false);
    const [removingKeys, setRemovingKeys] = useState(new Set());
    const [overrideWarning, setOverrideWarning] = useState(null);
    const [platform, setPlatform] = useState('mobile-sdk');
    const [providerId, setProviderId] = useState('scandit');
    const [scopeId, setScopeId] = useState(ACCOUNT_LEVEL);
    const [appIdentifier, setAppIdentifier] = useState('');
    const [licenseKey, setLicenseKey] = useState('');
    const [clientToken, setClientToken] = useState('');
    const [applicationId, setApplicationId] = useState('');
    useEffect(() => {
        const accountId = Number(id);
        if (!accountId) {
            setStatus('failed');
            return;
        }
        setStatus('loading');
        asyncDispatch(loadAccountVendorKeys(accountId))
            .then((vendorKeys) => {
            setRows(vendorKeys);
            setStatus('loaded');
        })
            .catch((error) => {
            console.error(error);
            setStatus('failed');
        });
        // Display names only: failing here must not cost us the key list.
        asyncDispatch(loadAdminAccountGroups(accountId))
            .then((groups) => {
            setGroupNames(new Map(groups.map((group) => [String(group.id), group.name])));
        })
            .catch(console.error);
    }, [id]);
    const accountGroupId = scopeId === ACCOUNT_LEVEL ? null : scopeId;
    const scopeOptions = useMemo(() => [
        { id: ACCOUNT_LEVEL, name: 'Account level (all groups)' },
        ...[...groupNames].map(([groupId, name]) => ({ id: groupId, name }))
    ], [groupNames]);
    const clearSecrets = useCallback(() => {
        setLicenseKey('');
        setClientToken('');
        setApplicationId('');
    }, []);
    // Prefill only when the platform/provider pair actually changes: a rows
    // refresh after a save or remove must not clobber in-progress typing.
    const syncedPair = useRef('');
    useEffect(() => {
        if (status !== 'loaded')
            return;
        const pair = `${platform}:${providerId}:${scopeId}`;
        if (syncedPair.current === pair)
            return;
        syncedPair.current = pair;
        const matches = rows.filter((row) => row.accountGroupId === accountGroupId &&
            row.platform === platform &&
            row.providerId === providerId);
        setAppIdentifier(matches.length === 1 ? (matches[0]?.appIdentifier ?? '') : '');
    }, [platform, providerId, scopeId, accountGroupId, rows, status]);
    const handlePlatformChange = useCallback((e) => {
        setPlatform(e.target.value);
        clearSecrets();
    }, [clearSecrets]);
    const handleProviderChange = useCallback((e) => {
        setProviderId(e.target.value);
        clearSecrets();
    }, [clearSecrets]);
    const handleScopeChange = useCallback((e) => {
        setScopeId(e.target.value);
        clearSecrets();
    }, [clearSecrets]);
    const handleAppIdentifierChange = useCallback((e) => {
        setAppIdentifier(e.target.value);
    }, []);
    const handleLicenseKeyChange = useCallback((e) => {
        setLicenseKey(e.target.value);
    }, []);
    const handleClientTokenChange = useCallback((e) => {
        setClientToken(e.target.value);
    }, []);
    const handleApplicationIdChange = useCallback((e) => {
        setApplicationId(e.target.value);
    }, []);
    const canSave = useMemo(() => {
        return providerId === 'scandit'
            ? licenseKey.trim().length > 0
            : clientToken.trim().length > 0 && applicationId.trim().length > 0;
    }, [providerId, licenseKey, clientToken, applicationId]);
    const handleSave = useCallback(() => {
        const accountId = Number(id);
        if (!accountId || status !== 'loaded')
            return;
        const normalizedAppIdentifier = appIdentifier.trim() || null;
        const isReplacing = rows.some((row) => row.accountGroupId === accountGroupId &&
            row.platform === platform &&
            row.providerId === providerId &&
            row.appIdentifier === normalizedAppIdentifier);
        if (isReplacing) {
            const platformLabel = labelOf(PLATFORMS, platform);
            const providerLabel = labelOf(PROVIDERS, providerId);
            const scopeLabel = accountGroupId === null
                ? 'this account'
                : `group ${groupNames.get(accountGroupId) ?? accountGroupId}`;
            const message = `An active ${providerLabel} key is already configured for ` +
                `${platformLabel} on ${scopeLabel}${appSuffix(normalizedAppIdentifier)}. Saving now overwrites it ` +
                'immediately: any integration still using the old key will stop ' +
                'authenticating, and this cannot be undone. Continue?';
            // eslint-disable-next-line no-alert
            if (!window.confirm(message)) {
                return;
            }
        }
        const values = providerId === 'scandit'
            ? { licenseKey }
            : { clientToken, applicationId };
        setIsSaving(true);
        setOverrideWarning(null);
        asyncDispatch(updateAccountVendorKey(accountId, {
            accountGroupId: accountGroupId === null ? null : Number(accountGroupId),
            platform,
            providerId,
            values,
            appIdentifier: normalizedAppIdentifier
        }))
            .then(async ({ groupOverrides }) => {
            if (groupOverrides.length > 0) {
                const names = groupOverrides
                    .map((groupId) => groupNames.get(groupId) ?? groupId)
                    .join(', ');
                setOverrideWarning(`Saved. Groups ${names} have their own ${labelOf(PROVIDERS, providerId)} key for ${labelOf(PLATFORMS, platform)}${appSuffix(normalizedAppIdentifier)}, so API keys scoped to those groups will keep using it.`);
            }
            clearSecrets();
            setAppIdentifier('');
            return asyncDispatch(loadAccountVendorKeys(accountId));
        })
            .then((vendorKeys) => {
            setRows(vendorKeys);
        })
            .catch(console.error)
            .finally(() => {
            setIsSaving(false);
        });
    }, [
        id,
        platform,
        providerId,
        licenseKey,
        clientToken,
        applicationId,
        appIdentifier,
        accountGroupId,
        groupNames,
        rows,
        status,
        asyncDispatch,
        clearSecrets
    ]);
    const handleRemove = useCallback((row) => {
        const accountId = Number(id);
        if (!accountId)
            return;
        const scope = row.accountGroupId === null
            ? 'for this account'
            : `for group ${groupNames.get(row.accountGroupId) ?? row.accountGroupId}`;
        if (
        // eslint-disable-next-line no-alert
        !window.confirm(`Remove the ${labelOf(PLATFORMS, row.platform)} / ${labelOf(PROVIDERS, row.providerId)} key ${scope}${appSuffix(row.appIdentifier)}? This cannot be undone.`)) {
            return;
        }
        const key = rowKey(row);
        setRemovingKeys((prev) => new Set([...prev, key]));
        asyncDispatch(deleteAccountVendorKey(accountId, {
            accountGroupId: row.accountGroupId,
            platform: row.platform,
            providerId: row.providerId,
            appIdentifier: row.appIdentifier
        }))
            .then(async () => asyncDispatch(loadAccountVendorKeys(accountId)))
            .then((vendorKeys) => {
            setRows(vendorKeys);
        })
            .catch(console.error)
            .finally(() => {
            setRemovingKeys((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        });
    }, [id, groupNames, asyncDispatch]);
    return (React.createElement("div", { className: "full-page" },
        React.createElement("div", { className: "groups-main" },
            React.createElement("h1", { className: "g-page-name" }, "SDK Vendor Keys"),
            React.createElement("div", { className: "vk-card", style: {
                    backgroundColor: tokens.colors.white,
                    boxShadow: tokens.shadows.hover
                } },
                React.createElement("div", { className: "vk-card-header" }, "Configured Keys"),
                status === 'loading' ? (React.createElement("p", { className: "vk-loading" }, "Loading...")) : status === 'failed' ? (React.createElement("div", { className: "vk-empty-state" },
                    React.createElement(ErrorOutlineIcon, { style: { color: tokens.colors.errorPrimary } }),
                    React.createElement("p", { className: "vk-empty-text", style: { color: tokens.colors.errorPrimary } }, "The configured keys could not be loaded, so saving is disabled. Reload the page to try again."))) : rows.length > 0 ? (rows.map((row) => {
                    const key = rowKey(row);
                    const platformLabel = labelOf(PLATFORMS, row.platform);
                    const providerLabel = labelOf(PROVIDERS, row.providerId);
                    return (React.createElement("div", { className: "vk-row", key: key },
                        React.createElement("div", { className: "vk-row-info" },
                            React.createElement("div", { className: "vk-row-heading" },
                                React.createElement("span", { className: "vk-badge", style: {
                                        backgroundColor: tokens.colors.lightPurple,
                                        color: tokens.colors.darkPurple
                                    } }, platformLabel),
                                React.createElement("span", { className: "vk-provider-name" }, providerLabel)),
                            React.createElement("div", { className: "vk-caption", style: { color: tokens.colors.gray800 } },
                                "App identifier: ",
                                row.appIdentifier ?? 'Not set',
                                " \u00B7",
                                ' ',
                                row.accountGroupId !== null && (React.createElement(React.Fragment, null,
                                    "Group:",
                                    ' ',
                                    groupNames.get(row.accountGroupId) ??
                                        row.accountGroupId,
                                    ' ',
                                    "\u00B7",
                                    ' ')),
                                "Updated ",
                                new Date(row.updatedAt).toLocaleString(),
                                row.updatedByEmail !== null && (React.createElement(React.Fragment, null,
                                    ' ',
                                    "by ",
                                    React.createElement("span", { "data-ph-mask": true }, row.updatedByEmail))))),
                        React.createElement(ButtonWithLoader, { className: "vk-remove-btn", color: "secondary", disabled: removingKeys.has(key), isLoading: removingKeys.has(key), name: "Remove", onClick: () => {
                                handleRemove(row);
                            } })));
                })) : (React.createElement("div", { className: "vk-empty-state" },
                    React.createElement(VpnKeyIcon, { style: { color: tokens.colors.gray600 } }),
                    React.createElement("p", { className: "vk-empty-text", style: { color: tokens.colors.gray800 } }, "No vendor keys are configured for this account.")))),
            React.createElement("div", { className: "vk-card", style: {
                    backgroundColor: tokens.colors.white,
                    boxShadow: tokens.shadows.hover
                } },
                React.createElement("div", { className: "vk-card-header" }, "Add or Replace a Key"),
                React.createElement("div", { className: "vk-card-body" },
                    React.createElement("div", { className: "vk-field-row" },
                        React.createElement(ReusableSelect, { isClearable: false, items: PLATFORMS, label: "Platform", onChange: handlePlatformChange, value: platform }),
                        React.createElement(ReusableSelect, { isClearable: false, items: PROVIDERS, label: "Provider", onChange: handleProviderChange, value: providerId }),
                        React.createElement(ReusableSelect, { isClearable: false, items: scopeOptions, label: "Scope", onChange: handleScopeChange, value: scopeId })),
                    React.createElement("p", { className: "vk-caption vk-combo-note", style: { color: tokens.colors.gray800 } }, "Only Mobile SDK + Scandit is resolved per account today. Other combinations are stored here but still served from global configuration."),
                    React.createElement("div", { className: "vk-secret-box", style: { backgroundColor: tokens.colors.gray50 } },
                        providerId === 'scandit' ? (React.createElement(TextField, { fullWidth: true, inputProps: { 'aria-describedby': 'vk-secret-caption' }, label: "License Key", multiline: true, onChange: handleLicenseKeyChange, value: licenseKey })) : (React.createElement("div", { className: "vk-field-row" },
                            React.createElement(TextField, { fullWidth: true, inputProps: { 'aria-describedby': 'vk-secret-caption' }, label: "Client Token", onChange: handleClientTokenChange, value: clientToken }),
                            React.createElement(TextField, { fullWidth: true, inputProps: { 'aria-describedby': 'vk-secret-caption' }, label: "Application ID", onChange: handleApplicationIdChange, value: applicationId }))),
                        React.createElement("p", { className: "vk-caption vk-secret-caption", id: "vk-secret-caption", style: { color: tokens.colors.gray800 } }, "Hidden for security, server-side only. Treat it like a password: the stored value is never loaded back here, entering a value replaces it.")),
                    React.createElement("div", { className: "vk-field-block" },
                        React.createElement(TextField, { fullWidth: true, inputProps: {
                                'aria-describedby': 'vk-app-identifier-caption',
                                maxLength: 255
                            }, label: "App Identifier", onChange: handleAppIdentifierChange, placeholder: "e.g. com.example.myapp", value: appIdentifier }),
                        React.createElement("p", { className: "vk-caption", id: "vk-app-identifier-caption", style: { color: tokens.colors.gray800 } }, "The bundle ID, package name, or domain this key is bound to, exactly as the app reports it (case-sensitive). Each identifier has its own key; leave empty for the default key. Use letters, digits, '.', '_' or '-', starting with a letter or digit.")),
                    overrideWarning !== null && (React.createElement("div", { className: "vk-warning", role: "status", style: {
                            backgroundColor: tokens.colors.warningBackground,
                            color: tokens.colors.warningPrimary
                        } },
                        React.createElement(ReportProblemOutlinedIcon, { fontSize: "small" }),
                        React.createElement("span", null, overrideWarning))),
                    React.createElement("div", { className: "vk-save-row" },
                        React.createElement(ButtonWithLoader, { disabled: isSaving || status !== 'loaded' || !canSave, isLoading: isSaving, name: "Save", onClick: handleSave })))))));
};
